import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";

import radioCheck from "assets/svg/radio-check.svg";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "components/ui/select";
import { formatTmNoForDisplay } from "utils/tmNumberDisplay";
import {
  getOppositionHearingConfirmation,
  saveOppositionHearingConfirmation,
  type OppositionHearingConfirmationDecision,
  type OppositionHearingConfirmationItem,
} from "api/opposition";

export const OPPOSITION_CONFIRMATION_FOR_HEARING_FORM_ID =
  "opposition-confirmation-for-hearing-form";

function RadioCircle({ active }: { active: boolean }) {
  if (active) return <img src={radioCheck} alt="" width={14} height={14} />;
  return (
    <span
      aria-hidden
      className="inline-block w-[14px] h-[14px] rounded-full border border-[#C3C3C3] bg-white"
    />
  );
}

type Props = {
  leadId?: string | null;
  onSuccess?: (leadId: string) => void;
};

export default function OppositionConfirmationFromClientForHearingView({
  leadId,
  onSuccess,
}: Props) {
  const [loading, setLoading] = useState<boolean>(!!leadId);
  const [saving, setSaving] = useState(false);
  const [marks, setMarks] = useState<OppositionHearingConfirmationItem[]>([]);
  const [decisions, setDecisions] = useState<
    Record<string, OppositionHearingConfirmationDecision | "">
  >({});
  const [activeIdx, setActiveIdx] = useState(0);

  useEffect(() => {
    if (!leadId) {
      setMarks([]);
      setDecisions({});
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    getOppositionHearingConfirmation(leadId)
      .then((data) => {
        if (cancelled) return;
        setMarks(data.conflictingMarks);
        const prefill: Record<string, OppositionHearingConfirmationDecision | ""> = {};
        data.conflictingMarks.forEach((m) => {
          prefill[m.id] = m.confirmation ?? "";
        });
        setDecisions(prefill);
        setActiveIdx(0);
      })
      .catch((err) => {
        if (!cancelled) {
          toast.error(
            err instanceof Error ? err.message : "Failed to load confirmation"
          );
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [leadId]);

  const activeMark = marks[activeIdx];

  const allDecided = useMemo(
    () =>
      marks.length > 0 &&
      marks.every((m) => decisions[m.id] && decisions[m.id] !== ""),
    [marks, decisions]
  );

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!leadId || saving) return;
    if (marks.length === 0) {
      toast.error(
        "No eligible applications. Set Date of Hearing in Step 26 first."
      );
      return;
    }
    if (!allDecided) {
      toast.error(
        "Select YES / NO / POSTPONE for every application before continuing."
      );
      return;
    }
    setSaving(true);
    try {
      await saveOppositionHearingConfirmation(leadId, {
        confirmations: marks.map((m) => ({
          conflictingMarkId: m.id,
          confirmation: decisions[m.id] as OppositionHearingConfirmationDecision,
        })),
      });
      toast.success("Client confirmation saved.");
      onSuccess?.(leadId);
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : "Failed to save confirmation"
      );
    } finally {
      setSaving(false);
    }
  };

  if (!leadId) {
    return (
      <div className="flex flex-col gap-[22px] w-full max-w-[1085px] py-12">
        <p className="text-sm text-[#6D6D6D]">
          Please complete the previous steps to access this step.
        </p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex flex-col gap-[22px] w-full max-w-[1085px] py-12">
        <p className="text-sm text-[#6D6D6D]">Loading…</p>
      </div>
    );
  }

  if (marks.length === 0) {
    return (
      <div className="flex flex-col gap-[22px] w-full max-w-[1085px] py-12">
        <p className="text-sm text-[#6D6D6D]">
          No eligible applications. Set Date of Hearing in Step 26 first.
        </p>
      </div>
    );
  }

  return (
    <form
      id={OPPOSITION_CONFIRMATION_FOR_HEARING_FORM_ID}
      onSubmit={handleSubmit}
      className="flex flex-col gap-[22px] w-full max-w-[1085px]"
      style={{ fontFamily: "Inter, sans-serif" }}
    >
      {marks.length > 1 && (
        <div className="flex flex-row flex-wrap gap-3">
          {marks.map((m, idx) => {
            const isActive = idx === activeIdx;
            return (
              <button
                key={m.id}
                type="button"
                onClick={() => setActiveIdx(idx)}
                className={`flex items-center gap-2 px-2.5 py-2.5 rounded-[6px] h-10 min-w-[140px] transition-colors text-xs ${
                  isActive
                    ? "bg-[#222222] text-white"
                    : "bg-[#F7F7F7] text-[#222222]"
                }`}
              >
                <RadioCircle active={isActive} />
                {formatTmNoForDisplay(m.applicationNo ?? "")}
              </button>
            );
          })}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-5 w-full">
        <div className="flex flex-col gap-1">
          <label className="text-xs font-normal text-[#222222] leading-5">
            Client Confirmation
          </label>
          <Select
            value={activeMark ? decisions[activeMark.id] || "" : ""}
            onValueChange={(v) => {
              if (!activeMark) return;
              setDecisions((prev) => ({
                ...prev,
                [activeMark.id]: v as OppositionHearingConfirmationDecision,
              }));
            }}
          >
            <SelectTrigger className="h-9 min-h-[44px] md:min-h-0 w-full text-[12px] md:text-sm rounded-[6px]">
              <SelectValue placeholder="Select" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="YES">Yes — Proceed</SelectItem>
              <SelectItem value="POSTPONE">Postpone — File TM-M</SelectItem>
              <SelectItem value="NO">No — Skip</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
    </form>
  );
}
